import React, { useState, useEffect } from 'react';
import { Header } from '../components/Header';
import { PageLoader } from '../components/LoadingSpinner';
import { formatCurrency, formatNumber, formatPercentage } from '../lib/formatters';
import { 
  Globe, Building2, Truck, Container, TrendingUp, TrendingDown, 
  Users, DollarSign, Search, Filter, Plus, Star, Shield, 
  Coins, BarChart3, ArrowRight, ExternalLink, Wallet,
  Building, Package, PieChart, Zap
} from 'lucide-react';
import { toast } from 'sonner';

// Sample data for the public marketplace
const SAMPLE_COMPANIES = [
  {
    company_id: 'hub_transport',
    name: 'HUB Transport LLC',
    logo: '🚛',
    verified: true,
    description: 'Premier trucking company specializing in long-haul freight',
    total_assets: 45,
    total_value: 4500000,
    investors: 127,
    avg_roi: 28.5,
    rating: 4.8,
    price_change_24h: 2.3,
    listed_since: '2024-06-15',
  },
  {
    company_id: 'swift_logistics',
    name: 'Swift Logistics Inc',
    logo: '⚡',
    verified: true,
    description: 'Fast and reliable regional transportation services',
    total_assets: 78,
    total_value: 7200000,
    investors: 342,
    avg_roi: 24.2,
    rating: 4.6,
    price_change_24h: -1.2,
    listed_since: '2024-03-20',
  },
  {
    company_id: 'mountain_freight',
    name: 'Mountain Freight Co',
    logo: '🏔️',
    verified: true,
    description: 'Specialized in mountain and difficult terrain deliveries',
    total_assets: 32,
    total_value: 2800000,
    investors: 89,
    avg_roi: 31.7,
    rating: 4.9,
    price_change_24h: 4.1,
    listed_since: '2024-08-10',
  },
  {
    company_id: 'coastal_carriers',
    name: 'Coastal Carriers',
    logo: '🌊',
    verified: false,
    description: 'Port-to-destination container transportation',
    total_assets: 56,
    total_value: 5100000,
    investors: 201,
    avg_roi: 22.8,
    rating: 4.4,
    price_change_24h: 0.8,
    listed_since: '2024-09-05',
  },
];

const SAMPLE_ASSETS = [
  { asset_id: 'pub_1', company: 'HUB Transport LLC', name: 'Peterbilt 579 #101', type: 'truck', price: 1250, total_shares: 100, available: 45, weekly_profit: 2800, roi: 32.5 },
  { asset_id: 'pub_2', company: 'Swift Logistics Inc', name: 'Kenworth T680 #205', type: 'truck', price: 1100, total_shares: 150, available: 72, weekly_profit: 2400, roi: 28.3 },
  { asset_id: 'pub_3', company: 'Mountain Freight Co', name: 'Freightliner Cascadia #089', type: 'truck', price: 1400, total_shares: 80, available: 20, weekly_profit: 3200, roi: 35.1 },
  { asset_id: 'pub_4', company: 'HUB Transport LLC', name: 'Wabash DuraPlate #T45', type: 'trailer', price: 450, total_shares: 200, available: 120, weekly_profit: 800, roi: 22.4 },
  { asset_id: 'pub_5', company: 'Coastal Carriers', name: 'Great Dane Container #C12', type: 'trailer', price: 520, total_shares: 180, available: 95, weekly_profit: 920, roi: 24.8 },
  { asset_id: 'pub_6', company: 'Swift Logistics Inc', name: 'Volvo VNL 860 #312', type: 'truck', price: 1350, total_shares: 120, available: 55, weekly_profit: 3100, roi: 33.2 },
];

const SAMPLE_FLEETS = [
  { fleet_id: 'fleet_1', name: 'HUB Premium Fleet', company: 'HUB Transport LLC', assets: 12, min_investment: 5000, avg_roi: 29.5, investors: 45 },
  { fleet_id: 'fleet_2', name: 'Swift Express Bundle', company: 'Swift Logistics Inc', assets: 8, min_investment: 3000, avg_roi: 26.8, investors: 67 },
  { fleet_id: 'fleet_3', name: 'Mountain Elite Pack', company: 'Mountain Freight Co', assets: 6, min_investment: 4000, avg_roi: 33.2, investors: 28 },
];

export default function PublicMarketplacePage() {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');

  // Platform stats
  const platformStats = {
    totalCompanies: SAMPLE_COMPANIES.length,
    totalAssets: SAMPLE_COMPANIES.reduce((sum, c) => sum + c.total_assets, 0),
    totalValue: SAMPLE_COMPANIES.reduce((sum, c) => sum + c.total_value, 0),
    totalInvestors: SAMPLE_COMPANIES.reduce((sum, c) => sum + c.investors, 0),
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: PieChart },
    { id: 'companies', label: 'Companies', icon: Building2 },
    { id: 'assets', label: 'Individual Assets', icon: Truck },
    { id: 'fleets', label: 'Fleet Bundles', icon: Package },
    { id: 'tokenize', label: 'Tokenization', icon: Coins },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <Header />

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-purple-600 via-blue-600 to-cyan-500 text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-12">
          <div className="flex items-center gap-2 md:gap-3 mb-3 md:mb-4">
            <Globe className="w-8 h-8 md:w-10 md:h-10" />
            <span className="px-2 py-1 md:px-3 bg-white/20 rounded-full text-xs md:text-sm font-semibold">PUBLIC BETA</span>
          </div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 md:mb-4">
            TruckStock Exchange
          </h1>
          <p className="text-base md:text-xl text-white/80 max-w-2xl mb-4 md:mb-6">
            The world's first public marketplace for investing in trucks, trailers, and transportation fleets. 
            Own a piece of the road.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
            <button className="w-full sm:w-auto px-5 py-3 bg-white text-purple-700 font-bold rounded-lg hover:bg-gray-100 transition-colors flex items-center justify-center gap-2">
              <Wallet className="w-5 h-5" />
              Connect Wallet
            </button>
            <button className="w-full sm:w-auto px-5 py-3 bg-white/20 text-white font-bold rounded-lg hover:bg-white/30 transition-colors flex items-center justify-center gap-2">
              <Building className="w-5 h-5" />
              List Your Company
            </button>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-8">
        {/* Platform Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-xl p-5 text-center">
            <Building2 className="w-8 h-8 text-purple-400 mx-auto mb-2" />
            <div className="text-3xl font-bold text-white font-mono">{platformStats.totalCompanies}</div>
            <div className="text-sm text-white/60">Listed Companies</div>
          </div>
          <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-xl p-5 text-center">
            <Truck className="w-8 h-8 text-blue-400 mx-auto mb-2" />
            <div className="text-3xl font-bold text-white font-mono">{platformStats.totalAssets}</div>
            <div className="text-sm text-white/60">Total Assets</div>
          </div>
          <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-xl p-5 text-center">
            <DollarSign className="w-8 h-8 text-green-400 mx-auto mb-2" />
            <div className="text-3xl font-bold text-white font-mono">${(platformStats.totalValue / 1000000).toFixed(1)}M</div>
            <div className="text-sm text-white/60">Total Value</div>
          </div>
          <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-xl p-5 text-center">
            <Users className="w-8 h-8 text-cyan-400 mx-auto mb-2" />
            <div className="text-3xl font-bold text-white font-mono">{formatNumber(platformStats.totalInvestors)}</div>
            <div className="text-sm text-white/60">Active Investors</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6 -mx-4 px-4 overflow-x-auto scrollbar-hide">
          <div className="flex gap-2 min-w-max pb-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 md:gap-2 px-3 md:px-5 py-2.5 md:py-3 rounded-lg font-medium text-sm md:text-base transition-all whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-white text-purple-700 shadow-lg'
                    : 'bg-white/10 text-white/70 hover:bg-white/20 hover:text-white'
                }`}
              >
                <tab.icon className="w-4 h-4 md:w-5 md:h-5" />
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* How It Works */}
            <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                <Zap className="w-7 h-7 text-yellow-400" />
                How TruckStock Works
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center p-6 bg-white/5 rounded-xl">
                  <div className="w-16 h-16 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Search className="w-8 h-8 text-purple-400" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">1. Browse & Research</h3>
                  <p className="text-white/60 text-sm">Explore verified companies, individual trucks, or fleet bundles. View performance data and ROI projections.</p>
                </div>
                <div className="text-center p-6 bg-white/5 rounded-xl">
                  <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Coins className="w-8 h-8 text-blue-400" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">2. Invest with Tokens</h3>
                  <p className="text-white/60 text-sm">Purchase tokenized shares using cryptocurrency or fiat. Each token represents real ownership in the asset.</p>
                </div>
                <div className="text-center p-6 bg-white/5 rounded-xl">
                  <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <TrendingUp className="w-8 h-8 text-green-400" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">3. Earn & Trade</h3>
                  <p className="text-white/60 text-sm">Receive revenue share from truck operations. Trade your tokens 24/7 on the marketplace.</p>
                </div>
              </div>
            </div>

            {/* Top Performing Companies */}
            <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6">
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <Star className="w-6 h-6 text-yellow-400" />
                Top Performing Companies
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {SAMPLE_COMPANIES.slice(0, 4).map((company) => (
                  <div key={company.company_id} className="bg-white/5 rounded-xl p-4 hover:bg-white/10 transition-colors cursor-pointer">
                    <div className="flex items-center gap-4">
                      <div className="text-4xl">{company.logo}</div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-bold text-white">{company.name}</h3>
                          {company.verified && <Shield className="w-4 h-4 text-blue-400" />}
                        </div>
                        <p className="text-sm text-white/60">{company.total_assets} assets • {company.investors} investors</p>
                      </div>
                      <div className="text-right">
                        <div className={`flex items-center gap-1 ${company.price_change_24h >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {company.price_change_24h >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                          <span className="font-bold">{company.price_change_24h >= 0 ? '+' : ''}{company.price_change_24h}%</span>
                        </div>
                        <div className="text-sm text-white/60">Avg ROI: {company.avg_roi}%</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Revenue Model */}
            <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30 rounded-2xl p-6">
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <DollarSign className="w-6 h-6 text-green-400" />
                Platform Revenue Model
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white/10 rounded-xl p-4">
                  <div className="text-2xl font-bold text-green-400 mb-1">2.5%</div>
                  <div className="text-white font-medium">Transaction Fee</div>
                  <div className="text-sm text-white/60">Per trade on marketplace</div>
                </div>
                <div className="bg-white/10 rounded-xl p-4">
                  <div className="text-2xl font-bold text-green-400 mb-1">$499/mo</div>
                  <div className="text-white font-medium">Company Listing</div>
                  <div className="text-sm text-white/60">Monthly subscription</div>
                </div>
                <div className="bg-white/10 rounded-xl p-4">
                  <div className="text-2xl font-bold text-green-400 mb-1">$29/mo</div>
                  <div className="text-white font-medium">Premium Investor</div>
                  <div className="text-sm text-white/60">Advanced analytics</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'companies' && (
          <div className="space-y-6">
            {/* Search & Filter */}
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <input
                  type="text"
                  placeholder="Search companies..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <button className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-xl flex items-center gap-2">
                <Plus className="w-5 h-5" />
                List Your Company
              </button>
            </div>

            {/* Company Cards */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {SAMPLE_COMPANIES.map((company) => (
                <div key={company.company_id} className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl overflow-hidden hover:border-purple-500/50 transition-all">
                  <div className="p-6">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="text-5xl">{company.logo}</div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-xl font-bold text-white">{company.name}</h3>
                          {company.verified && (
                            <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs font-bold rounded-full flex items-center gap-1">
                              <Shield className="w-3 h-3" /> VERIFIED
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-white/60">{company.description}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-4 gap-3 mb-4">
                      <div className="bg-white/5 rounded-lg p-3 text-center">
                        <div className="text-lg font-bold text-white">{company.total_assets}</div>
                        <div className="text-xs text-white/50">Assets</div>
                      </div>
                      <div className="bg-white/5 rounded-lg p-3 text-center">
                        <div className="text-lg font-bold text-white">${(company.total_value / 1000000).toFixed(1)}M</div>
                        <div className="text-xs text-white/50">Value</div>
                      </div>
                      <div className="bg-white/5 rounded-lg p-3 text-center">
                        <div className="text-lg font-bold text-green-400">{company.avg_roi}%</div>
                        <div className="text-xs text-white/50">Avg ROI</div>
                      </div>
                      <div className="bg-white/5 rounded-lg p-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Star className="w-4 h-4 text-yellow-400" />
                          <span className="text-lg font-bold text-white">{company.rating}</span>
                        </div>
                        <div className="text-xs text-white/50">Rating</div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-white/60">
                        <Users className="w-4 h-4" />
                        {company.investors} investors
                      </div>
                      <button className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg flex items-center gap-2 transition-colors">
                        View Assets <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'assets' && (
          <div className="space-y-6">
            {/* Filters */}
            <div className="flex flex-wrap gap-3">
              <button 
                onClick={() => setFilterType('all')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${filterType === 'all' ? 'bg-white text-purple-700' : 'bg-white/10 text-white/70 hover:bg-white/20'}`}
              >
                All Assets
              </button>
              <button 
                onClick={() => setFilterType('truck')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${filterType === 'truck' ? 'bg-white text-purple-700' : 'bg-white/10 text-white/70 hover:bg-white/20'}`}
              >
                <Truck className="w-4 h-4" /> Trucks
              </button>
              <button 
                onClick={() => setFilterType('trailer')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${filterType === 'trailer' ? 'bg-white text-purple-700' : 'bg-white/10 text-white/70 hover:bg-white/20'}`}
              >
                <Container className="w-4 h-4" /> Trailers
              </button>
            </div>

            {/* Asset Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {SAMPLE_ASSETS.filter(a => filterType === 'all' || a.type === filterType).map((asset) => (
                <div key={asset.asset_id} className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-xl p-5 hover:border-purple-500/50 transition-all cursor-pointer">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-white/10 rounded-lg">
                      {asset.type === 'truck' ? <Truck className="w-6 h-6 text-blue-400" /> : <Container className="w-6 h-6 text-amber-400" />}
                    </div>
                    <div>
                      <h3 className="font-semibold text-white">{asset.name}</h3>
                      <p className="text-xs text-white/50">{asset.company}</p>
                    </div>
                  </div>

                  <div className="space-y-3 mb-4">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-white/60">Price per Share</span>
                      <span className="text-lg font-bold text-white font-mono">${formatNumber(asset.price)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-white/60">Available</span>
                      <span className="text-white font-mono">{asset.available} / {asset.total_shares}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-white/60">Weekly Profit</span>
                      <span className="text-green-400 font-mono">+${formatNumber(asset.weekly_profit)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-white/60">Est. Annual ROI</span>
                      <span className="text-green-400 font-bold">{asset.roi}%</span>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="mb-4">
                    <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-purple-500 to-blue-500 rounded-full"
                        style={{ width: `${((asset.total_shares - asset.available) / asset.total_shares) * 100}%` }}
                      />
                    </div>
                    <div className="text-xs text-white/50 mt-1 text-right">
                      {Math.round(((asset.total_shares - asset.available) / asset.total_shares) * 100)}% sold
                    </div>
                  </div>

                  <button className="w-full py-2.5 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition-colors">
                    Invest Now
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'fleets' && (
          <div className="space-y-6">
            <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6 mb-6">
              <h2 className="text-xl font-bold text-white mb-2">Fleet Bundles</h2>
              <p className="text-white/60">Diversify your investment across multiple assets with curated fleet bundles</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {SAMPLE_FLEETS.map((fleet) => (
                <div key={fleet.fleet_id} className="bg-gradient-to-br from-purple-500/20 to-blue-500/20 border border-purple-500/30 rounded-2xl p-6 hover:border-purple-500/60 transition-all">
                  <div className="flex items-center gap-2 mb-4">
                    <Package className="w-6 h-6 text-purple-400" />
                    <h3 className="text-xl font-bold text-white">{fleet.name}</h3>
                  </div>
                  <p className="text-sm text-white/60 mb-4">{fleet.company}</p>
                  
                  <div className="space-y-3 mb-6">
                    <div className="flex justify-between">
                      <span className="text-white/60">Assets Included</span>
                      <span className="text-white font-bold">{fleet.assets}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/60">Min Investment</span>
                      <span className="text-white font-bold font-mono">${formatNumber(fleet.min_investment)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/60">Avg Annual ROI</span>
                      <span className="text-green-400 font-bold">{fleet.avg_roi}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/60">Investors</span>
                      <span className="text-white">{fleet.investors}</span>
                    </div>
                  </div>

                  <button className="w-full py-3 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-xl transition-colors">
                    Invest in Bundle
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'tokenize' && (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-2xl p-8">
              <div className="flex items-center gap-3 mb-4">
                <Coins className="w-10 h-10 text-amber-400" />
                <h2 className="text-2xl font-bold text-white">Asset Tokenization</h2>
              </div>
              <p className="text-white/70 text-lg mb-6">
                Every asset on TruckStock is tokenized on the blockchain, providing transparent ownership, 
                instant transfers, and 24/7 trading capability.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-white/10 rounded-xl p-6">
                  <h3 className="text-lg font-bold text-white mb-4">Revenue Sharing Model</h3>
                  <ul className="space-y-3 text-white/70">
                    <li className="flex items-start gap-2">
                      <span className="text-green-400">✓</span>
                      Token holders receive weekly profit distributions
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-400">✓</span>
                      Smart contracts automate payments
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-400">✓</span>
                      No securities registration required
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-400">✓</span>
                      Transparent on-chain accounting
                    </li>
                  </ul>
                </div>

                <div className="bg-white/10 rounded-xl p-6">
                  <h3 className="text-lg font-bold text-white mb-4">Token Benefits</h3>
                  <ul className="space-y-3 text-white/70">
                    <li className="flex items-start gap-2">
                      <span className="text-blue-400">✓</span>
                      Trade 24/7 on decentralized exchanges
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-400">✓</span>
                      Fractional ownership (buy any amount)
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-400">✓</span>
                      Instant settlement (no waiting periods)
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-400">✓</span>
                      Global accessibility
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-white/10 rounded-xl p-6">
                <h3 className="text-lg font-bold text-white mb-4">Supported Wallets & Chains</h3>
                <div className="flex flex-wrap gap-3">
                  <span className="px-4 py-2 bg-white/10 rounded-lg text-white">MetaMask</span>
                  <span className="px-4 py-2 bg-white/10 rounded-lg text-white">Coinbase Wallet</span>
                  <span className="px-4 py-2 bg-white/10 rounded-lg text-white">WalletConnect</span>
                  <span className="px-4 py-2 bg-white/10 rounded-lg text-white">Ethereum</span>
                  <span className="px-4 py-2 bg-white/10 rounded-lg text-white">Polygon</span>
                  <span className="px-4 py-2 bg-white/10 rounded-lg text-white">Base</span>
                </div>
              </div>
            </div>

            {/* For Companies */}
            <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-white mb-4">For Transportation Companies</h2>
              <p className="text-white/60 mb-6">List your fleet on TruckStock and access a new source of capital</p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-purple-400 mb-2">$0</div>
                  <div className="text-white">Upfront Cost</div>
                  <div className="text-sm text-white/50">Pay only when you raise capital</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-purple-400 mb-2">48hrs</div>
                  <div className="text-white">Quick Listing</div>
                  <div className="text-sm text-white/50">Get listed in 2 business days</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-purple-400 mb-2">100%</div>
                  <div className="text-white">Asset Control</div>
                  <div className="text-sm text-white/50">You maintain full operations</div>
                </div>
              </div>

              <button className="w-full py-4 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2">
                <Building2 className="w-5 h-5" />
                Apply to List Your Company
              </button>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 mt-12">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2 text-white">
              <Globe className="w-6 h-6 text-purple-400" />
              <span className="font-bold">TruckStock Exchange</span>
            </div>
            <div className="text-white/50 text-sm">
              © 2026 TruckStock. Democratizing transportation investment.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
